import { useMemo } from 'react';
import { useArenaStore } from '../../store/arena-store';
import type { ArenaListItem } from '../../types/arena';

/**
 * L'arena in cui si sta GIOCANDO, che non è per forza quella che si guarda.
 *
 * `setArenaMatch` non tocca `currentArena`, `loadArena` sì: chi è in coda in A
 * e apre B dall'elenco resta accoppiabile in A con `currentArena = B`. Le
 * schermate di partita che leggevano `currentArena` prendevano quindi da B la
 * cadenza, il badge «Valutata», il conto alla rovescia, lo stato dell'arena
 * nel riquadro finale e perfino il proprio rating — che si sceglie sul bucket
 * variante+cadenza, quindi il bucket sbagliato.
 *
 * Il nome della partita è `arenaPartitaId` (lo scrivono `setArenaMatch` e
 * `rejoinGame`); i dati si ripescano dall'elenco che lo store ha già. Il
 * ripiego su `currentArena` copre due casi veri: non si sta giocando affatto,
 * e l'elenco non contiene quell'arena — chi arriva su una partita d'arena
 * senza essere passato dalla lista (un rientro dopo un ricarico) la trova solo
 * in `currentArena`, che `rejoinGame` fa caricare per conto suo.
 *
 * Non è un selettore dello store apposta: restituire un oggetto calcolato da
 * dentro `useArenaStore` darebbe un riferimento nuovo a ogni classifica che
 * arriva — e in arena ne arriva una ogni due secondi — ridisegnando la
 * scacchiera per niente. Qui i tre campi si selezionano singolarmente e la
 * scelta si fa con `useMemo`.
 */
export function useArenaPartita(): ArenaListItem | null {
  const currentArena = useArenaStore((s) => s.currentArena);
  const arenaPartitaId = useArenaStore((s) => s.arenaPartitaId);
  const arenas = useArenaStore((s) => s.arenas);

  return useMemo(() => {
    if (!arenaPartitaId || currentArena?.id === arenaPartitaId) return currentArena;
    return arenas.find((a) => a.id === arenaPartitaId) ?? currentArena;
  }, [arenaPartitaId, currentArena, arenas]);
}
